/**
 * The one card every chat artifact renders as — a dashboard or an audience.
 *
 * Layout, which is the audience family generalised (its single filled CTA is the one thing both types need,
 * and the tile + title + badge line reads the same for either):
 *
 *     [tile] Title [badge]                    [ Primary CTA → ]
 *            second line (meta)
 *
 * Default: the tile sits at the START of the title, not vertically centred on the whole card — the same
 * placement the ask-tool summaries in the transcript use (`items-start` + a `mt-0.5` `rounded-md` tile). A
 * centred tile drifts away from the title the moment a second line appears.
 *
 * `compact` is the copy PINNED to the top of the transcript once the inline card scrolls past it
 * (ACT2-538). It is this card collapsed to its FIRST LINE — tile, title, badge, CTA — and elevated with a
 * shadow so it floats over the messages. With no second line, the row is `items-center` (and the tile
 * loses its `mt-0.5`) so the 20px title sits on the same midline as the 24px tile and CTA. The meta line
 * is dropped rather than shrunk: pinned, the card's only job is to be the way back to the artifact, and
 * the artifact's own page shows that detail better.
 *
 * `bodyAction` makes the WHOLE card (except the CTA) the click target — a stretched overlay, not a second
 * button in the title row. That is how a dashboard opens its in-chat preview: click anywhere, hover the
 * whole card. The CTA sits above the overlay so "Go to …" is a separate hit target.
 *
 * Presentational only. Each type's adapter owns what goes in the badge and meta slots and where the CTA
 * leads, so this file never learns what an audience or a dashboard is.
 */
import { ArrowRight, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import { ActionTooltip } from '@/_shared/components/ActionTooltip';
import { Button } from '@/_shared/components/ui/button';
import { cn } from '@/_shared/lib/utils';

/**
 * Where the CTA leads. A union rather than two optional fields, so "navigates" and "calls back" cannot both
 * be set (`to` would silently win), and the third arm makes "there is nowhere to go" an explicit choice.
 */
export type ArtifactCardAction = { label: string } & (
  | { to: string; onSelect?: never; disabledReason?: never }
  | { onSelect: () => void; to?: never; disabledReason?: never }
  /** No destination — the CTA renders disabled, and `disabledReason` is the tooltip that says why. */
  | { to?: never; onSelect?: never; disabledReason: string }
);

export interface ArtifactCardBodyAction {
  /** Accessible name for the stretched overlay that turns the card into a click target. */
  ariaLabel: string;
  /** Short affordance on the first line, e.g. "Open preview". Decorative — the overlay owns the click. */
  hint: string;
  onSelect: () => void;
}

export interface ArtifactCardProps {
  /** A `lucide-react` element; the card sizes it and supplies the tile around it. */
  icon: React.ReactNode;
  title: string;
  /** Sits inline after the title on the first line, so it survives `compact`. */
  badge?: React.ReactNode;
  /** The second line. Dropped entirely in `compact`. */
  meta?: React.ReactNode;
  /**
   * The primary CTA. Omit it entirely when there is nowhere to go (an unsaved dashboard has no page).
   * With neither `to` nor `onSelect` it renders disabled rather than hidden — a malformed payload
   * should still show where the affordance would be (ui skill rule 10).
   */
  action?: ArtifactCardAction;
  /** Turns the whole card, except the CTA, into a click target — dashboards use it to open the preview. */
  bodyAction?: ArtifactCardBodyAction;
  variant?: 'default' | 'compact';
  className?: string;
}

function Body({
  title,
  badge,
  meta,
  hint,
}: {
  title: string;
  badge?: React.ReactNode;
  meta?: React.ReactNode;
  hint?: string;
}): React.ReactElement {
  return (
    <>
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <span className="min-w-0 truncate text-sm font-medium text-foreground">{title}</span>
          {badge}
        </div>
        {meta ? (
          <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-2 text-xs text-muted-foreground">{meta}</div>
        ) : null}
      </div>
      {hint ? (
        <span className="flex shrink-0 items-center gap-0.5 text-xs text-muted-foreground" aria-hidden>
          {hint}
          <ChevronRight className="size-3" aria-hidden />
        </span>
      ) : null}
    </>
  );
}

export function ArtifactCard({
  icon,
  title,
  badge,
  meta,
  action,
  bodyAction,
  variant = 'default',
  className,
}: ArtifactCardProps): React.ReactElement {
  const compact = variant === 'compact';
  const canOpen = action !== undefined && (action.to !== undefined || action.onSelect !== undefined);
  const size = compact ? 'sm' : 'md';
  // A disabled control can't receive pointer events, so the reason has to come from a wrapper — the shared
  // ActionTooltip pattern (ui skill rule 10: keep it visible, disabled, and say why).
  const disabledReason = action !== undefined && !canOpen ? (action.disabledReason ?? '') : '';

  return (
    <div
      className={cn(
        'relative flex w-full rounded-xl border border-border bg-card',
        compact ? 'items-center gap-2 rounded-lg px-2.5 py-1.5 shadow-sm' : 'items-start gap-2.5 px-3.5 py-2.5',
        bodyAction && 'cursor-pointer transition-colors hover:bg-muted',
        className,
      )}
    >
      {bodyAction ? (
        // Stretched overlay — sibling of the CTA, not its parent — so the card is one hit target without
        // nesting a link inside a button. The CTA sits above it (`z-10`) and keeps its own click.
        <button
          type="button"
          onClick={bodyAction.onSelect}
          aria-label={bodyAction.ariaLabel}
          // `focus-ring` is the app-wide keyboard focus treatment; the secondary shadow it used to carry is
          // the hover ring, which reads as a mouse state on the card's largest hit target.
          className="absolute inset-0 z-0 cursor-pointer rounded-[inherit] border-0 bg-transparent focus-ring"
        />
      ) : null}

      <span
        className={cn(
          // Default: same placement as the ask-tool card — a `rounded-md` tile aligned to the title,
          // not a centred square that floats between two lines. Compact is a single line, so the tile
          // shares the row midline and does not need the `mt-0.5` title-cap offset.
          'relative flex shrink-0 items-center justify-center rounded-md bg-muted text-foreground',
          compact ? 'size-6 [&>svg]:size-3.5' : 'mt-0.5 size-7 [&>svg]:size-4',
          // Let the overlay receive the pointer so the title does not show an I-beam over a clickable card.
          bodyAction && 'pointer-events-none',
        )}
        aria-hidden
      >
        {icon}
      </span>

      <div className={cn('relative flex min-w-0 flex-1 items-center gap-2.5', bodyAction && 'pointer-events-none')}>
        <Body title={title} badge={badge} meta={compact ? undefined : meta} hint={bodyAction?.hint} />
      </div>

      {action !== undefined ? (
        // `relative z-10` lifts the CTA above the overlay so "Go to …" is its own hit target.
        // `self-center` keeps the button on the card's midline; only the tile follows the title.
        <div className="relative z-10 shrink-0 self-center">
          {action.to !== undefined ? (
            // `asChild` renders the child alone — Button's own `suffixIcon` slot is skipped on that path — so
            // the arrow has to live inside the Link. Button's `[&_svg]` rule still sizes it.
            <Button variant="primary" size={size} asChild className="select-none">
              <Link to={action.to} aria-label={`${action.label}: ${title}`}>
                {action.label}
                <ArrowRight />
              </Link>
            </Button>
          ) : (
            <ActionTooltip show={!canOpen} message={disabledReason}>
              <Button
                variant={canOpen ? 'primary' : 'secondary'}
                size={size}
                disabled={!canOpen}
                onClick={action.onSelect}
                // Name it by title even when disabled — two malformed cards in one transcript would
                // otherwise expose two controls with the identical accessible name.
                aria-label={`${action.label}: ${title}`}
                suffixIcon={<ArrowRight />}
              >
                {action.label}
              </Button>
            </ActionTooltip>
          )}
        </div>
      ) : null}
    </div>
  );
}
